/**
 * AnimationPanel — Advanced Animation Studio
 *
 * Full-featured animation panel with:
 * - Entrance animations with preview
 * - Scroll-triggered animations (IntersectionObserver)
 * - Hover effect presets
 * - Easing curve selection
 * - Stagger delay for child elements
 * - Loop/repeat controls
 * - Exit animations
 * - Live animation preview per section
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  X, Sparkles, Play, Eye, ChevronDown, ChevronUp,
  Repeat, MousePointer, Scroll, Zap, Clock, Maximize2, RotateCcw
} from 'lucide-react';

// ── Animation Presets ────────────────────────────────────────────────
const ENTRANCE_PRESETS = [
  { value: 'none', label: 'None' },
  { value: 'fadeIn', label: 'Fade In' },
  { value: 'fadeInUp', label: 'Fade In Up' },
  { value: 'fadeInDown', label: 'Fade In Down' },
  { value: 'fadeInLeft', label: 'Fade In Left' },
  { value: 'fadeInRight', label: 'Fade In Right' },
  { value: 'zoomIn', label: 'Zoom In' },
  { value: 'zoomInUp', label: 'Zoom In Up' },
  { value: 'slideInUp', label: 'Slide In Up' },
  { value: 'slideInDown', label: 'Slide In Down' },
  { value: 'slideInLeft', label: 'Slide In Left' },
  { value: 'slideInRight', label: 'Slide In Right' },
  { value: 'bounceIn', label: 'Bounce In' },
  { value: 'bounceInUp', label: 'Bounce In Up' },
  { value: 'flipInX', label: 'Flip Horizontal' },
  { value: 'flipInY', label: 'Flip Vertical' },
  { value: 'rotateIn', label: 'Rotate In' },
  { value: 'lightSpeedIn', label: 'Speed Fly In' },
  { value: 'jackInTheBox', label: 'Pop & Spin' },
  { value: 'rollIn', label: 'Roll In' },
];

const EXIT_PRESETS = [
  { value: 'none', label: 'None' },
  { value: 'fadeOut', label: 'Fade Out' },
  { value: 'fadeOutUp', label: 'Fade Out Up' },
  { value: 'fadeOutDown', label: 'Fade Out Down' },
  { value: 'zoomOut', label: 'Zoom Out' },
  { value: 'slideOutUp', label: 'Slide Out Up' },
  { value: 'slideOutDown', label: 'Slide Out Down' },
  { value: 'bounceOut', label: 'Bounce Out' },
];

const HOVER_PRESETS = [
  { value: 'none', label: 'None' },
  { value: 'grow', label: 'Grow', css: 'transform: scale(1.05)' },
  { value: 'shrink', label: 'Shrink', css: 'transform: scale(0.95)' },
  { value: 'lift', label: 'Lift Up', css: 'transform: translateY(-8px); box-shadow: 0 12px 32px rgba(0,0,0,0.15)' },
  { value: 'glow', label: 'Glow', css: 'box-shadow: 0 0 20px rgba(99,102,241,0.4)' },
  { value: 'brighten', label: 'Brighten', css: 'filter: brightness(1.1)' },
  { value: 'darken', label: 'Darken', css: 'filter: brightness(0.9)' },
  { value: 'blur-bg', label: 'Blur Background', css: 'backdrop-filter: blur(4px)' },
  { value: 'border-pop', label: 'Border Pop', css: 'border-color: var(--color-primary); box-shadow: 0 0 0 3px var(--color-primary-light)' },
  { value: 'tilt-left', label: 'Tilt Left', css: 'transform: rotate(-3deg)' },
  { value: 'tilt-right', label: 'Tilt Right', css: 'transform: rotate(3deg)' },
];

const EASING_PRESETS = [
  { value: 'ease', label: 'Ease' },
  { value: 'ease-in', label: 'Ease In' },
  { value: 'ease-out', label: 'Ease Out' },
  { value: 'ease-in-out', label: 'Ease In Out' },
  { value: 'linear', label: 'Linear' },
  { value: 'cubic-bezier(0.4, 0, 0.2, 1)', label: 'Smooth' },
  { value: 'cubic-bezier(0.16, 1, 0.3, 1)', label: 'Quick Start' },
  { value: 'cubic-bezier(0.68, -0.55, 0.27, 1.55)', label: 'Spring Back' },
  { value: 'cubic-bezier(0.34, 1.56, 0.64, 1)', label: 'Overshoot' },
  { value: 'cubic-bezier(0.22, 1, 0.36, 1)', label: 'Gentle Stop' },
];

const DELAY_OPTIONS = [
  { value: '0', label: 'None' },
  { value: '0.1', label: '0.1s' },
  { value: '0.2', label: '0.2s' },
  { value: '0.3', label: '0.3s' },
  { value: '0.4', label: '0.4s' },
  { value: '0.5', label: '0.5s' },
  { value: '0.6', label: '0.6s' },
  { value: '0.8', label: '0.8s' },
  { value: '1', label: '1s' },
  { value: '1.5', label: '1.5s' },
  { value: '2', label: '2s' },
];

const DURATION_OPTIONS = [
  { value: '0.3', label: 'Snappy (0.3s)' },
  { value: '0.5', label: 'Fast (0.5s)' },
  { value: '0.8', label: 'Normal (0.8s)' },
  { value: '1', label: 'Slow (1s)' },
  { value: '1.5', label: 'Very Slow (1.5s)' },
  { value: '2', label: 'Cinematic (2s)' },
];

const STAGGER_OPTIONS = [
  { value: '0', label: 'None' },
  { value: '0.05', label: '50ms' },
  { value: '0.1', label: '100ms' },
  { value: '0.15', label: '150ms' },
  { value: '0.2', label: '200ms' },
  { value: '0.3', label: '300ms' },
];

const SCROLL_THRESHOLD_OPTIONS = [
  { value: '0', label: 'Immediately visible' },
  { value: '0.1', label: '10% visible' },
  { value: '0.25', label: '25% visible' },
  { value: '0.5', label: '50% visible' },
  { value: '0.75', label: '75% visible' },
];

// ── Styles ───────────────────────────────────────────────────────────
const s = {
  panel: {
    width: 340, background: '#ffffff', borderLeft: '1px solid #e5e7eb',
    height: '100%', display: 'flex', flexDirection: 'column',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: {
    padding: '12px 16px', background: '#1e293b', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
  },
  headerLeft: {
    display: 'flex', alignItems: 'center', gap: 8,
  },
  closeBtn: {
    background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
    padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center',
  },
  body: {
    flex: 1, overflowY: 'auto', padding: 0,
  },
  sectionCard: {
    borderBottom: '1px solid #f0f0f0',
  },
  sectionHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    width: '100%', padding: '10px 16px', border: 'none', background: '#ffffff',
    cursor: 'pointer', transition: 'background 0.15s',
  },
  sectionHeaderLeft: {
    display: 'flex', alignItems: 'center', gap: 8,
  },
  sectionName: {
    fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'capitalize',
  },
  sectionBadge: (active) => ({
    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
    padding: '2px 6px', borderRadius: 4,
    background: active ? '#dcfce7' : '#f1f5f9',
    color: active ? '#16a34a' : '#94a3b8',
  }),
  controlBlock: {
    padding: '0 16px 12px',
  },
  label: {
    fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 4, fontWeight: 500,
  },
  select: {
    width: '100%', padding: '7px 10px', borderRadius: 8,
    border: '1px solid #e5e7eb', fontSize: 12, color: '#374151',
    background: '#fff', outline: 'none', cursor: 'pointer',
    transition: 'border-color 0.15s',
    fontFamily: "'Inter', sans-serif",
  },
  row: {
    display: 'flex', gap: 8, marginBottom: 8,
  },
  halfCol: {
    flex: 1,
  },
  previewBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    width: '100%', padding: '7px 0', borderRadius: 8,
    border: '1px solid #e0e7ff', background: '#f5f3ff',
    color: '#6366f1', fontSize: 11, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
  },
  checkbox: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
    cursor: 'pointer', fontSize: 12, color: '#374151',
  },
  resetBtn: {
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '4px 8px', borderRadius: 6,
    border: '1px solid #fee2e2', background: '#fff',
    color: '#ef4444', fontSize: 10, fontWeight: 500,
    cursor: 'pointer', transition: 'all 0.15s',
  },
};

// ── Collapsible Section ──────────────────────────────────────────────
function AnimSection({ title, icon: Icon, children, defaultOpen = true, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid #f0f0f0' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ ...s.sectionHeader, background: open ? '#f8f9fa' : '#fff' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f8f9fa'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = open ? '#f8f9fa' : '#fff'; }}
      >
        <div style={s.sectionHeaderLeft}>
          <ChevronDown
            size={14}
            style={{ color: '#9ca3af', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}
          />
          {Icon && <Icon size={14} style={{ color: '#9ca3af' }} />}
          <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{title}</span>
          {badge && <span style={s.sectionBadge(badge !== 'none')}>{badge !== 'none' ? badge : 'Off'}</span>}
        </div>
      </button>
      {open && <div style={s.controlBlock}>{children}</div>}
    </div>
  );
}

// ── Block Card ───────────────────────────────────────────────────────
function BlockAnimationCard({ block, index, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const previewRef = useRef(null);
  const anim = block.animation || {
    entrance: 'none', exit: 'none', hover: 'none',
    delay: '0', duration: '0.8', easing: 'ease-out',
    scrollTrigger: false, scrollThreshold: '0.1',
    stagger: '0', loop: false, loopCount: 1,
  };

  const sectionName = (block.id || `Section ${index + 1}`).replace(/[-_]/g, ' ');
  // Support both old format (type) and new format (entrance)
  const entranceType = anim.entrance || anim.type || 'none';
  const hasAnimation = entranceType !== 'none' || (anim.hover && anim.hover !== 'none');

  const handleChange = useCallback((field, value) => {
    const newBlock = { ...block };
    if (!newBlock.animation) {
      newBlock.animation = {
        entrance: 'none', exit: 'none', hover: 'none',
        delay: '0', duration: '0.8', easing: 'ease-out',
        scrollTrigger: false, scrollThreshold: '0.1',
        stagger: '0', loop: false, loopCount: 1,
      };
    }
    newBlock.animation = { ...newBlock.animation, [field]: value };
    // Keep backward compat: sync 'type' with 'entrance'
    if (field === 'entrance') newBlock.animation.type = value;
    onUpdate(index, newBlock);
  }, [block, index, onUpdate]);

  const handleReset = useCallback(() => {
    const newBlock = { ...block };
    newBlock.animation = {
      entrance: 'none', exit: 'none', hover: 'none', type: 'none',
      delay: '0', duration: '0.8', easing: 'ease-out',
      scrollTrigger: false, scrollThreshold: '0.1',
      stagger: '0', loop: false, loopCount: 1,
    };
    onUpdate(index, newBlock);
  }, [block, index, onUpdate]);

  // Map of animation preset names to editor keyframe names
  const ANIM_MAP = {
    fadeIn: 'editorFadeIn', fadeInUp: 'editorFadeInUp', fadeInDown: 'editorFadeInDown',
    fadeInLeft: 'editorFadeInLeft', fadeInRight: 'editorFadeInRight',
    zoomIn: 'editorZoomIn', zoomInUp: 'editorZoomInUp',
    slideInUp: 'editorSlideInUp', slideInDown: 'editorSlideInDown',
    slideInLeft: 'editorSlideInLeft', slideInRight: 'editorSlideInRight',
    bounceIn: 'editorBounceIn', bounceInUp: 'editorBounceInUp',
    flipInX: 'editorFlipInX', flipInY: 'editorFlipInY',
    rotateIn: 'editorRotateIn', lightSpeedIn: 'editorLightSpeedIn',
    jackInTheBox: 'editorJackInTheBox', rollIn: 'editorRollIn',
  };

  // Preview animation on the section in the editor
  const handlePreview = useCallback(() => {
    // Find the section DOM element by block id or index
    const blockId = block.id;
    let el = blockId ? document.querySelector(`[data-block-id="${blockId}"]`) : null;
    if (!el) {
      // Fallback: find by section wrapper index
      const wrappers = document.querySelectorAll('[data-section-index]');
      el = wrappers[index];
    }
    if (!el) return;

    const keyframeName = ANIM_MAP[entranceType];
    if (!keyframeName) return;

    // Reset any existing animation first
    el.style.animation = 'none';
    // Force reflow to restart animation
    void el.offsetHeight;

    const duration = anim.duration || '0.8';
    const easing = anim.easing || 'ease-out';
    el.style.animation = `${keyframeName} ${duration}s ${easing} both`;

    const cleanup = () => {
      el.style.animation = '';
    };
    el.addEventListener('animationend', cleanup, { once: true });
    // Safety fallback
    setTimeout(cleanup, 4000);
  }, [block.id, index, entranceType, anim.duration, anim.easing]);

  return (
    <div style={{
      marginBottom: 8, background: expanded ? '#f8f9fa' : '#ffffff',
      borderRadius: 10, border: '1px solid', borderColor: hasAnimation ? '#c7d2fe' : '#e5e7eb',
      overflow: 'hidden', transition: 'all 0.2s',
    }}>
      {/* Card Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 12px', cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background: hasAnimation ? '#6366f1' : '#d1d5db',
            flexShrink: 0,
          }} />
          <span style={{
            fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'capitalize',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {sectionName}
          </span>
          {hasAnimation && (
            <span style={{
              fontSize: 9, fontWeight: 600, color: '#6366f1', background: '#eef2ff',
              padding: '1px 6px', borderRadius: 4, textTransform: 'capitalize', flexShrink: 0,
            }}>
              {entranceType.replace(/([A-Z])/g, ' $1').trim()}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {hasAnimation && (
            <button
              onClick={(e) => { e.stopPropagation(); handlePreview(); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 6,
                border: '1px solid #e0e7ff', background: '#f5f3ff',
                color: '#6366f1', cursor: 'pointer', padding: 0,
              }}
              title="Preview animation"
            >
              <Play size={11} />
            </button>
          )}
          <ChevronDown
            size={14}
            style={{
              color: '#9ca3af', transition: 'transform 0.2s',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </div>
      </div>

      {/* Expanded Controls */}
      {expanded && (
        <div style={{ padding: '0 12px 12px', borderTop: '1px solid #f0f0f0' }}>

          {/* ── Entrance Animation ── */}
          <AnimSection title="Entrance" icon={Zap} badge={entranceType}>
            <div style={{ marginBottom: 8 }}>
              <label style={s.label}>Effect</label>
              <select
                value={entranceType}
                onChange={(e) => handleChange('entrance', e.target.value)}
                style={s.select}
              >
                {ENTRANCE_PRESETS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            {entranceType !== 'none' && (
              <>
                <div style={s.row}>
                  <div style={s.halfCol}>
                    <label style={s.label}>Duration</label>
                    <select value={anim.duration || '0.8'} onChange={(e) => handleChange('duration', e.target.value)} style={s.select}>
                      {DURATION_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                  <div style={s.halfCol}>
                    <label style={s.label}>Delay</label>
                    <select value={anim.delay || '0'} onChange={(e) => handleChange('delay', e.target.value)} style={s.select}>
                      {DELAY_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <label style={s.label}>Motion Style</label>
                  <select value={anim.easing || 'ease-out'} onChange={(e) => handleChange('easing', e.target.value)} style={s.select}>
                    {EASING_PRESETS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </select>
                </div>

                <div style={s.row}>
                  <div style={s.halfCol}>
                    <label style={s.label}>Cascade Delay</label>
                    <select value={anim.stagger || '0'} onChange={(e) => handleChange('stagger', e.target.value)} style={s.select}>
                      {STAGGER_OPTIONS.map(st => <option key={st.value} value={st.value}>{st.label}</option>)}
                    </select>
                  </div>
                  <div style={s.halfCol}>
                    <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Repeat size={10} /> Loop
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={!!anim.loop}
                        onChange={(e) => handleChange('loop', e.target.checked)}
                        style={{ accentColor: '#6366f1', cursor: 'pointer' }}
                      />
                      {anim.loop && (
                        <input
                          type="number"
                          min="1" max="99"
                          value={anim.loopCount || 1}
                          onChange={(e) => handleChange('loopCount', parseInt(e.target.value) || 1)}
                          placeholder="∞"
                          style={{ ...s.select, width: 60, padding: '4px 6px' }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </AnimSection>

          {/* ── Scroll Trigger ── */}
          <AnimSection title="Scroll Trigger" icon={Scroll} defaultOpen={!!anim.scrollTrigger}>
            <div style={s.checkbox} onClick={() => handleChange('scrollTrigger', !anim.scrollTrigger)}>
              <input
                type="checkbox"
                checked={!!anim.scrollTrigger}
                onChange={() => {}}
                style={{ accentColor: '#6366f1', cursor: 'pointer' }}
              />
              <span style={{ fontSize: 12, color: '#374151' }}>Animate when scrolled into view</span>
            </div>
            {anim.scrollTrigger && (
              <div style={{ marginBottom: 8 }}>
                <label style={s.label}>Visibility Threshold</label>
                <select
                  value={anim.scrollThreshold || '0.1'}
                  onChange={(e) => handleChange('scrollThreshold', e.target.value)}
                  style={s.select}
                >
                  {SCROLL_THRESHOLD_OPTIONS.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 4, lineHeight: 1.4 }}>
                  Animation plays when this % of the section is visible in the viewport.
                </p>
              </div>
            )}
          </AnimSection>

          {/* ── Hover Effect ── */}
          <AnimSection title="Hover Effect" icon={MousePointer} badge={anim.hover || 'none'} defaultOpen={false}>
            <div style={{ marginBottom: 8 }}>
              <label style={s.label}>On Hover</label>
              <select
                value={anim.hover || 'none'}
                onChange={(e) => handleChange('hover', e.target.value)}
                style={s.select}
              >
                {HOVER_PRESETS.map(h => (
                  <option key={h.value} value={h.value}>{h.label}</option>
                ))}
              </select>
            </div>
            {anim.hover && anim.hover !== 'none' && (
              <p style={{ fontSize: 10, color: '#9ca3af', lineHeight: 1.4 }}>
                Visitors will see this effect when they move their cursor over this section.
              </p>
            )}
          </AnimSection>

          {/* ── Exit Animation ── */}
          <AnimSection title="Exit Animation" icon={Eye} badge={anim.exit || 'none'} defaultOpen={false}>
            <div style={{ marginBottom: 8 }}>
              <label style={s.label}>On Scroll Away</label>
              <select
                value={anim.exit || 'none'}
                onChange={(e) => handleChange('exit', e.target.value)}
                style={s.select}
              >
                {EXIT_PRESETS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </AnimSection>

          {/* ── Preview & Reset ── */}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {hasAnimation && (
              <button onClick={handlePreview} style={{ ...s.previewBtn, flex: 1 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#ede9fe'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#f5f3ff'; }}
              >
                <Play size={12} /> Preview
              </button>
            )}
            {hasAnimation && (
              <button onClick={handleReset} style={{ ...s.resetBtn }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
              >
                <RotateCcw size={10} /> Reset
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Panel ───────────────────────────────────────────────────────
export default function AnimationPanel({ blocks, onUpdateBlock, onClose }) {
  const animatedCount = (blocks || []).filter(b =>
    b.animation && (
      (b.animation.entrance && b.animation.entrance !== 'none') ||
      (b.animation.type && b.animation.type !== 'none') ||
      (b.animation.hover && b.animation.hover !== 'none')
    )
  ).length;

  if (!blocks || blocks.length === 0) {
    return (
      <div style={s.panel}>
        <div style={s.header}>
          <div style={s.headerLeft}>
            <Sparkles style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Animations</span>
          </div>
          <button onClick={onClose} style={s.closeBtn}
            onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', textAlign: 'center' }}>
          <div>
            <Sparkles style={{ width: 40, height: 40, color: '#cbd5e1', marginBottom: 12 }} />
            <p style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>No sections to animate</p>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, lineHeight: 1.5 }}>
              Add sections to your page first, then come back to animate them.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.panel}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <Sparkles style={{ width: 16, height: 16 }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Animations</span>
          {animatedCount > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700, background: 'rgba(99,102,241,0.2)',
              color: '#a5b4fc', padding: '1px 6px', borderRadius: 4,
            }}>
              {animatedCount} active
            </span>
          )}
        </div>
        <button onClick={onClose} style={s.closeBtn}
          onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; }}
        >
          <X style={{ width: 16, height: 16 }} />
        </button>
      </div>

      {/* Info banner */}
      <div style={{
        padding: '8px 16px', background: '#f0fdf4', borderBottom: '1px solid #dcfce7',
        fontSize: 11, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <Zap size={12} />
        Click a section to expand animation controls
      </div>

      {/* Section list */}
      <div style={s.body}>
        <div style={{ padding: '12px' }}>
          {blocks.map((block, idx) => (
            <BlockAnimationCard
              key={block.id || idx}
              block={block}
              index={idx}
              onUpdate={onUpdateBlock}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
